// Demo data for the demo site
// This data is loaded fresh each time the demo page loads

// Default stage configuration template
const createDefaultStageConfig = (duration, sensorOverrides = {}) => ({
  duration,
  sensors: {
    ph: { optimalMin: 5.8, optimalMax: 6.5 },
    ec: { optimalMin: 1.2, optimalMax: 2.0 },
    tds: { optimalMin: 600, optimalMax: 1000 },
    waterTemp: { optimalMin: 65, optimalMax: 72 },
    orp: { optimalMin: 300, optimalMax: 500 },
    airTemp: { optimalMin: 72, optimalMax: 82 },
    humidity: { optimalMin: 50, optimalMax: 70 },
    co2: { optimalMin: 800, optimalMax: 1200 },
    vpd: { optimalMin: 0.8, optimalMax: 1.2 },
    ppfd: { optimalMin: 600, optimalMax: 1000 },
    dli: { optimalMin: 30, optimalMax: 50 },
    ...sensorOverrides,
  },
});

// Demo presets
export const DEMO_PRESETS = [
  {
    id: 'demo-preset-pepper',
    name: 'Hot Pepper Grow',
    description: 'Optimized settings for hot pepper varieties',
    stages: {
      seedling: createDefaultStageConfig(21, {
        ph: { optimalMin: 6.0, optimalMax: 6.5 },
        ec: { optimalMin: 0.5, optimalMax: 1.0 },
        tds: { optimalMin: 250, optimalMax: 500 },
        airTemp: { optimalMin: 75, optimalMax: 85 },
        humidity: { optimalMin: 60, optimalMax: 70 },
        ppfd: { optimalMin: 200, optimalMax: 400 },
        dli: { optimalMin: 15, optimalMax: 25 },
      }),
      vegetative: createDefaultStageConfig(35, {
        ph: { optimalMin: 6.0, optimalMax: 6.8 },
        ec: { optimalMin: 1.0, optimalMax: 1.8 },
        tds: { optimalMin: 500, optimalMax: 900 },
        airTemp: { optimalMin: 70, optimalMax: 85 },
        humidity: { optimalMin: 50, optimalMax: 65 },
        ppfd: { optimalMin: 400, optimalMax: 600 },
        dli: { optimalMin: 25, optimalMax: 40 },
      }),
      flower: createDefaultStageConfig(45, {
        ph: { optimalMin: 6.0, optimalMax: 6.5 },
        ec: { optimalMin: 1.5, optimalMax: 2.2 },
        tds: { optimalMin: 750, optimalMax: 1100 },
        airTemp: { optimalMin: 70, optimalMax: 85 },
        humidity: { optimalMin: 40, optimalMax: 55 },
        ppfd: { optimalMin: 500, optimalMax: 800 },
        dli: { optimalMin: 30, optimalMax: 45 },
      }),
      lateFlower: createDefaultStageConfig(30, {
        ph: { optimalMin: 6.0, optimalMax: 6.5 },
        ec: { optimalMin: 1.2, optimalMax: 1.8 },
        tds: { optimalMin: 600, optimalMax: 900 },
        airTemp: { optimalMin: 68, optimalMax: 80 },
        humidity: { optimalMin: 40, optimalMax: 50 },
        ppfd: { optimalMin: 500, optimalMax: 700 },
        dli: { optimalMin: 28, optimalMax: 40 },
      }),
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'demo-preset-indoor',
    name: 'General Indoor Grow',
    description: 'Balanced settings suitable for most indoor plants',
    stages: {
      seedling: createDefaultStageConfig(14, {
        ph: { optimalMin: 5.8, optimalMax: 6.2 },
        ec: { optimalMin: 0.5, optimalMax: 0.8 },
        tds: { optimalMin: 250, optimalMax: 400 },
        airTemp: { optimalMin: 75, optimalMax: 80 },
        humidity: { optimalMin: 65, optimalMax: 70 },
        ppfd: { optimalMin: 200, optimalMax: 400 },
        dli: { optimalMin: 12, optimalMax: 20 },
      }),
      vegetative: createDefaultStageConfig(28, {
        ph: { optimalMin: 5.8, optimalMax: 6.5 },
        ec: { optimalMin: 1.0, optimalMax: 1.6 },
        tds: { optimalMin: 500, optimalMax: 800 },
        airTemp: { optimalMin: 72, optimalMax: 82 },
        humidity: { optimalMin: 50, optimalMax: 70 },
        ppfd: { optimalMin: 400, optimalMax: 600 },
        dli: { optimalMin: 25, optimalMax: 35 },
      }),
      flower: createDefaultStageConfig(35, {
        ph: { optimalMin: 6.0, optimalMax: 6.5 },
        ec: { optimalMin: 1.4, optimalMax: 2.0 },
        tds: { optimalMin: 700, optimalMax: 1000 },
        airTemp: { optimalMin: 68, optimalMax: 78 },
        humidity: { optimalMin: 40, optimalMax: 50 },
        ppfd: { optimalMin: 600, optimalMax: 900 },
        dli: { optimalMin: 35, optimalMax: 45 },
      }),
      lateFlower: createDefaultStageConfig(21, {
        ph: { optimalMin: 6.0, optimalMax: 6.5 },
        ec: { optimalMin: 1.0, optimalMax: 1.6 },
        tds: { optimalMin: 500, optimalMax: 800 },
        airTemp: { optimalMin: 65, optimalMax: 75 },
        humidity: { optimalMin: 30, optimalMax: 40 },
        ppfd: { optimalMin: 600, optimalMax: 800 },
        dli: { optimalMin: 30, optimalMax: 40 },
      }),
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'demo-preset-hydro',
    name: 'Hydroponic High Performance',
    description: 'Optimized for DWC and NFT hydroponic systems',
    stages: {
      seedling: createDefaultStageConfig(10, {
        ph: { optimalMin: 5.5, optimalMax: 6.0 },
        ec: { optimalMin: 0.4, optimalMax: 0.6 },
        waterTemp: { optimalMin: 65, optimalMax: 68 },
      }),
      vegetative: createDefaultStageConfig(35, {
        ph: { optimalMin: 5.5, optimalMax: 6.2 },
        ec: { optimalMin: 1.2, optimalMax: 1.8 },
        waterTemp: { optimalMin: 65, optimalMax: 70 },
      }),
      flower: createDefaultStageConfig(42, {
        ph: { optimalMin: 5.8, optimalMax: 6.3 },
        ec: { optimalMin: 1.6, optimalMax: 2.2 },
        waterTemp: { optimalMin: 65, optimalMax: 70 },
      }),
      lateFlower: createDefaultStageConfig(28, {
        ph: { optimalMin: 6.0, optimalMax: 6.5 },
        ec: { optimalMin: 0.8, optimalMax: 1.4 },
        waterTemp: { optimalMin: 65, optimalMax: 68 },
      }),
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// Demo grows - includes one active grow to demonstrate features
const getDateDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
};

export const DEMO_GROWS = [
  // ==================== ACTIVE GROWS ====================
  {
    id: 'demo-grow-1',
    name: 'Buttercrunch Lettuce',
    presetId: 'demo-preset-indoor',
    startDate: getDateDaysAgo(45), // Started 45 days ago
    currentStage: 'flower',
    status: 'active',
    endDate: null,
    traits: {
      plantType: 'lettuce',
      environment: 'indoor',
      medium: 'dwc',
      lightingType: 'LED',
      areaSize: '4x4',
      description: 'First grow using the DWC system. Lettuce heads are forming nicely and looking healthy.',
      tags: ['DWC', 'LED', 'First Grow'],
    },
    dashboardConfig: {
      showOverview: true,
      showReadings: true,
      sections: [
        {
          id: 'section-water',
          title: 'Water Reservoir',
          sensors: [
            { type: 'ph', order: 0 },
            { type: 'ec', order: 1 },
            { type: 'waterTemp', order: 2 },
            { type: 'tds', order: 3 },
          ],
        },
        {
          id: 'section-atmosphere',
          title: 'Grow Room Atmosphere',
          sensors: [
            { type: 'airTemp', order: 0 },
            { type: 'humidity', order: 1 },
            { type: 'vpd', order: 2 },
            { type: 'co2', order: 3 },
          ],
        },
        {
          id: 'section-lighting',
          title: 'Lighting',
          sensors: [
            { type: 'ppfd', order: 0 },
            { type: 'dli', order: 1 },
          ],
        },
      ],
    },
    reminders: [
      {
        id: 'reminder-1',
        label: 'Change reservoir',
        icon: 'water',
        intervalHours: 168, // Weekly
        lastReset: getDateDaysAgo(3) + 'T10:00:00Z',
      },
      {
        id: 'reminder-2',
        label: 'Check pH',
        icon: 'science',
        intervalHours: 24, // Daily
        lastReset: getDateDaysAgo(0) + 'T08:00:00Z',
      },
      {
        id: 'reminder-3',
        label: 'Harvest outer leaves',
        icon: 'plant',
        intervalHours: 336, // Every 2 weeks
        lastReset: getDateDaysAgo(10) + 'T14:00:00Z',
      },
    ],
    createdAt: getDateDaysAgo(45) + 'T00:00:00Z',
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'demo-grow-2',
    name: 'Carolina Reaper',
    presetId: 'demo-preset-pepper',
    startDate: getDateDaysAgo(28), // Started 28 days ago
    currentStage: 'vegetative',
    status: 'active',
    endDate: null,
    traits: {
      plantType: 'pepper',
      environment: 'indoor',
      medium: 'coco',
      lightingType: 'LED',
      areaSize: '2x2',
      description: 'Growing some of the world\'s hottest peppers! Carolina Reapers in coco coir with automated fertigation.',
      tags: ['Hot Pepper', 'Coco', 'Automated'],
    },
    dashboardConfig: {
      showOverview: true,
      showReadings: true,
      sections: [
        {
          id: 'section-water-pepper',
          title: 'Nutrient Solution',
          sensors: [
            { type: 'ph', order: 0 },
            { type: 'ec', order: 1 },
            { type: 'tds', order: 2 },
          ],
        },
        {
          id: 'section-atmosphere-pepper',
          title: 'Environment',
          sensors: [
            { type: 'airTemp', order: 0 },
            { type: 'humidity', order: 1 },
            { type: 'vpd', order: 2 },
          ],
        },
      ],
    },
    reminders: [
      {
        id: 'reminder-pepper-1',
        label: 'Fertigation',
        icon: 'water',
        intervalHours: 12,
        lastReset: getDateDaysAgo(0) + 'T06:00:00Z',
      },
      {
        id: 'reminder-pepper-2',
        label: 'Check runoff EC',
        icon: 'science',
        intervalHours: 48,
        lastReset: getDateDaysAgo(1) + 'T10:00:00Z',
      },
    ],
    createdAt: getDateDaysAgo(28) + 'T00:00:00Z',
    updatedAt: new Date().toISOString(),
  },
  // ==================== COMPLETED PAST GROWS ====================
  {
    id: 'demo-grow-past-1',
    name: 'San Marzano Tomatoes',
    presetId: 'demo-preset-indoor',
    startDate: getDateDaysAgo(180), // Started 180 days ago
    currentStage: 'lateFlower',
    status: 'completed',
    endDate: getDateDaysAgo(82) + 'T00:00:00Z', // Completed ~82 days ago (98 day grow)
    traits: {
      plantType: 'tomato',
      environment: 'indoor',
      medium: 'dwc',
      lightingType: 'LED',
      areaSize: '4x4',
      description: 'Excellent San Marzano tomato grow. Harvested 35 lbs of tomatoes!',
      tags: ['DWC', 'LED', 'Tomato'],
    },
    dashboardConfig: {
      showOverview: true,
      showReadings: true,
      sections: [
        {
          id: 'section-water-bd',
          title: 'Water Reservoir',
          sensors: [
            { type: 'ph', order: 0 },
            { type: 'ec', order: 1 },
            { type: 'waterTemp', order: 2 },
          ],
        },
        {
          id: 'section-atmosphere-bd',
          title: 'Atmosphere',
          sensors: [
            { type: 'airTemp', order: 0 },
            { type: 'humidity', order: 1 },
            { type: 'co2', order: 2 },
          ],
        },
      ],
    },
    reminders: [],
    createdAt: getDateDaysAgo(180) + 'T00:00:00Z',
    updatedAt: getDateDaysAgo(82) + 'T00:00:00Z',
  },
  {
    id: 'demo-grow-past-2',
    name: 'Ghost Pepper',
    presetId: 'demo-preset-pepper',
    startDate: getDateDaysAgo(220), // Started 220 days ago
    currentStage: 'lateFlower',
    status: 'completed',
    endDate: getDateDaysAgo(89) + 'T00:00:00Z', // Completed ~89 days ago (131 day grow)
    traits: {
      plantType: 'pepper',
      environment: 'indoor',
      medium: 'soil',
      lightingType: 'HPS',
      areaSize: '3x3',
      description: 'Ghost peppers grown in organic soil. Produced over 100 peppers!',
      tags: ['Hot Pepper', 'Organic', 'Soil'],
    },
    dashboardConfig: {
      showOverview: true,
      showReadings: true,
      sections: [
        {
          id: 'section-env-gp',
          title: 'Environment',
          sensors: [
            { type: 'airTemp', order: 0 },
            { type: 'humidity', order: 1 },
          ],
        },
      ],
    },
    reminders: [],
    createdAt: getDateDaysAgo(220) + 'T00:00:00Z',
    updatedAt: getDateDaysAgo(89) + 'T00:00:00Z',
  },
  {
    id: 'demo-grow-past-3',
    name: 'Beefsteak Tomatoes',
    presetId: 'demo-preset-hydro',
    startDate: getDateDaysAgo(300), // Started 300 days ago
    currentStage: 'lateFlower',
    status: 'completed',
    endDate: getDateDaysAgo(185) + 'T00:00:00Z', // Completed ~185 days ago (115 day grow)
    traits: {
      plantType: 'tomato',
      environment: 'greenhouse',
      medium: 'nft',
      lightingType: 'Natural + LED',
      areaSize: '4x8',
      description: 'Hydroponic beefsteak tomatoes in NFT system. Great flavor, harvested 45 lbs!',
      tags: ['NFT', 'Hydro', 'Tomato', 'Greenhouse'],
    },
    dashboardConfig: {
      showOverview: true,
      showReadings: true,
      sections: [
        {
          id: 'section-water-tomato',
          title: 'NFT System',
          sensors: [
            { type: 'ph', order: 0 },
            { type: 'ec', order: 1 },
            { type: 'waterTemp', order: 2 },
          ],
        },
        {
          id: 'section-atmosphere-tomato',
          title: 'Greenhouse Climate',
          sensors: [
            { type: 'airTemp', order: 0 },
            { type: 'humidity', order: 1 },
            { type: 'vpd', order: 2 },
          ],
        },
      ],
    },
    reminders: [],
    createdAt: getDateDaysAgo(300) + 'T00:00:00Z',
    updatedAt: getDateDaysAgo(185) + 'T00:00:00Z',
  },
  {
    id: 'demo-grow-past-4',
    name: 'Habanero Batch',
    presetId: 'demo-preset-pepper',
    startDate: getDateDaysAgo(150), // Started 150 days ago
    currentStage: 'flower',
    status: 'cancelled', // This one was cancelled early
    endDate: getDateDaysAgo(95) + 'T00:00:00Z', // Cancelled 95 days ago
    traits: {
      plantType: 'pepper',
      environment: 'greenhouse',
      medium: 'soil',
      lightingType: 'Natural',
      areaSize: '6x6',
      description: 'Habanero grow cancelled due to pest infestation. Lesson learned about IPM!',
      tags: ['Pepper', 'Greenhouse', 'Cancelled'],
    },
    dashboardConfig: {
      showOverview: true,
      showReadings: true,
      sections: [],
    },
    reminders: [],
    createdAt: getDateDaysAgo(150) + 'T00:00:00Z',
    updatedAt: getDateDaysAgo(95) + 'T00:00:00Z',
  },
];

export const DEMO_ACTIVE_GROW_ID = 'demo-grow-1';

// Demo user profile
export const DEMO_USER = {
  uid: 'demo-user',
  email: 'demo@mygrowcollective.com',
  displayName: 'Demo User',
  isDemo: true,
};

// Demo sensor readings (simulated current values)
export const DEMO_SENSOR_READINGS = {
  ph: 6.2,
  ec: 1.4,
  tds: 720,
  waterTemp: 68,
  orp: 380,
  airTemp: 76,
  humidity: 55,
  co2: 950,
  vpd: 1.05,
  ppfd: 650,
  dli: 38,
};

// Function to get fresh demo data (called on each demo page load)
export function getDemoData() {
  return {
    presets: [...DEMO_PRESETS],
    grows: [...DEMO_GROWS.map(g => ({
      ...g,
      reminders: [...(g.reminders || [])],
      dashboardConfig: {
        ...g.dashboardConfig,
        sections: [...(g.dashboardConfig?.sections || [])],
      },
    }))],
    activeGrowId: DEMO_ACTIVE_GROW_ID,
    user: { ...DEMO_USER },
    sensorReadings: { ...DEMO_SENSOR_READINGS },
  };
}

// Get only active grows (for menu display)
export function getActiveGrows() {
  return DEMO_GROWS.filter(g => g.status === 'active');
}

// Get past/completed grows (for history/calendar)
export function getPastGrows() {
  return DEMO_GROWS.filter(g => g.status === 'completed' || g.status === 'cancelled');
}
